const STATIC_ASSET_URL_PATTERN = /\/_next\/static\/[^"'\s\\]+?\.(?:css|js)/gu;

const IMAGES_PER_PAGE = 48;

export const pickAssetUrls = (html: string) =>
  [...html.matchAll(STATIC_ASSET_URL_PATTERN)].map((match) => match[0]);

const pickSrcsetCandidate = (srcset: string, target: number) => {
  const candidates = srcset
    .split(',')
    .map((entry) => entry.trim().split(/\s+/u))
    .flatMap(([url, descriptor]) =>
      url ? [{ url, width: Number.parseInt(descriptor ?? '', 10) || 0 }] : []
    );

  if (candidates.length === 0) {
    return undefined;
  }

  let best = candidates[0];

  for (const candidate of candidates.slice(1)) {
    const isCloser =
      best.width < target
        ? candidate.width > best.width
        : candidate.width >= target && candidate.width < best.width;

    if (isCloser) {
      best = candidate;
    }
  }

  return best.url;
};

// The browser picks a srcset candidate for the slot the image occupies, not for the
// viewport, so the `sizes` attribute has to be resolved the same way here.
const resolveSlotWidth = (sizes: null | string) => {
  for (const entry of sizes?.split(',') ?? []) {
    const trimmed = entry.trim();
    const boundary = trimmed.lastIndexOf(' ');
    const condition = boundary === -1 ? '' : trimmed.slice(0, boundary).trim();
    const length = boundary === -1 ? trimmed : trimmed.slice(boundary + 1).trim();

    if (condition && !window.matchMedia(condition).matches) {
      continue;
    }

    const value = Number.parseFloat(length);

    if (Number.isNaN(value)) {
      continue;
    }

    if (length.endsWith('vw')) {
      return (window.innerWidth * value) / 100;
    }

    if (length.endsWith('px')) {
      return value;
    }
  }

  return window.innerWidth;
};

export const pickImageUrls = (html: string, pathname: string) => {
  const density = window.devicePixelRatio || 1;
  const base = new URL(pathname, location.origin);
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const urls = new Set<string>();

  for (const image of Array.from(parsed.querySelectorAll('img'))) {
    if (urls.size >= IMAGES_PER_PAGE) {
      break;
    }

    const srcset = image.getAttribute('srcset');
    const target = Math.ceil(resolveSlotWidth(image.getAttribute('sizes')) * density);
    const raw =
      (srcset ? pickSrcsetCandidate(srcset, target) : undefined) ?? image.getAttribute('src');

    if (!raw) {
      continue;
    }

    try {
      urls.add(new URL(raw, base).href);
    } catch {
      continue;
    }
  }

  return [...urls];
};
